import { useEffect } from "react";
import PropTypes from "prop-types";
import { Stack } from "@mui/material";
import { TopNavItem } from "./top-nav-item";
import { CustomDropdown } from "src/components/inputs";
import { useAuth } from "src/hooks/use-auth";
// User Permissions
import {
  inboundProcessPermission,
  priceSetupTomorrowCrewPermissions,
  linesheetSettingsTomorrowCrewPermissions,
  clientManagementTomorrowCrewPermission,
  bookingSystemTomorrowCrewPermissions,
  groupTypes,
} from "src/guards/role-based-guard";

export const TopNavSection = (props) => {
  const { items = [], pathname } = props;

  const { user } = useAuth();

  const userPermissions = user?.permissions;
  const userGroup = user?.group?.key;

  // JavaScript to enable horizontal scrolling with mouse wheel
  useEffect(() => {
    const scrollContainer = document.getElementById(
      "topnav-tabs-horizontal-scroll"
    );

    scrollContainer?.addEventListener("wheel", (event) => {
      event.preventDefault();
      scrollContainer.scrollLeft += event.deltaY / 3;
    });
  }, []);

  const generatePriceSetupTabsBasedOnUserPermissions = () => {
    // Finance / Operations / Sales Tabs
    const financeTab = { label: "Finance", value: "finance" };
    const operationsTab = {
      label: "Operations",
      value: "operations",
    };
    const salesTab = { label: "Sales", value: "sales" };

    // User Tabs based on Permissions
    const userTabs = [];

    // Tomorrow Crew Admin
    const isAdmin = userPermissions?.find(
      (object) => object.key === priceSetupTomorrowCrewPermissions.admin
    );

    if (isAdmin) {
      userTabs.push(financeTab, operationsTab, salesTab);
      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    // Price Management Admin
    const hasAdminPermission = userPermissions?.find(
      (object) =>
        object.key === priceSetupTomorrowCrewPermissions.priceManagementAdmin
    );

    if (hasAdminPermission) {
      userTabs.push(financeTab, operationsTab, salesTab);
      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    // Pre sales campaign linesheet / Pre sales campaign price list
    const hasCampaignLinesheetOrCampaignPriceListPermission =
      userPermissions?.find(
        (object) =>
          object.key ===
          priceSetupTomorrowCrewPermissions.preSalesCampaignLinesheet ||
          object.key ===
          priceSetupTomorrowCrewPermissions.preSalesCampaignPriceList
      );

    if (hasCampaignLinesheetOrCampaignPriceListPermission) {
      userTabs.push(financeTab, operationsTab, salesTab);
      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    // Price Management Finance
    const hasFinancePermission = userPermissions?.find(
      (object) =>
        object.key === priceSetupTomorrowCrewPermissions.priceManagementFinance
    );

    if (!hasAdminPermission && hasFinancePermission) {
      userTabs.push(financeTab);
    }

    // Price Management Operations
    const hasOperationsPermission = userPermissions?.find(
      (object) =>
        object.key ===
        priceSetupTomorrowCrewPermissions.priceManagementOperations
    );

    if (!hasAdminPermission && hasOperationsPermission) {
      userTabs.push(operationsTab);
    }

    // Price Management Sales
    const hasSalesPermission = userPermissions?.find(
      (object) =>
        object.key === priceSetupTomorrowCrewPermissions.priceManagementSales
    );

    if (!hasAdminPermission && hasSalesPermission) {
      userTabs.push(salesTab);
    }

    return { userTabs, selectedTab: userTabs[0]?.value };
  };

  const generateLinesheetSettingsTabsBasedOnUserPermissions = () => {
    // Blank Template / Zedonk Mappings
    const blankTemplateTab = {
      label: "Blank Template",
      value: "blank-template",
    };
    const zedonkMappingsTab = {
      label: "Zedonk Mappings",
      value: "zedonk-mappings",
    };

    // User Tabs based on Permissions
    const userTabs = [];

    // Tomorrow Crew Admin
    const hasTemplatePermissions = userPermissions?.find(
      (object) =>
        object.key === linesheetSettingsTomorrowCrewPermissions.admin ||
        object.key ===
        linesheetSettingsTomorrowCrewPermissions.linesheetSettingsTemplate
    );

    const hasZedonkPermissions = userPermissions?.find(
      (object) =>
        object.key === linesheetSettingsTomorrowCrewPermissions.admin ||
        object.key ===
        linesheetSettingsTomorrowCrewPermissions.linesheetSettingsZedonk
    );

    if (hasTemplatePermissions || hasZedonkPermissions) {
      if (hasTemplatePermissions) {
        userTabs.push(blankTemplateTab);
      }

      if (hasZedonkPermissions) {
        userTabs.push(zedonkMappingsTab);
      }

      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    return userTabs;
  };

  const generateInboundElementsTabsBasedOnUserPermissions = () => {
    // Requests / ASN
    const requestsTab = {
      label: "Requests",
      value: "requests",
    };
    const asnTab = {
      label: "ASN",
      value: "asn",
    };

    // User Tabs based on Permissions
    const userTabs = [];

    // Tomorrow Crew Admin
    const hasRequestsPermissions = userPermissions?.find(
      (object) =>
        object.key === inboundProcessPermission.admin ||
        object.key === inboundProcessPermission.operationsInbound
    );

    const hasASNPermissions = userPermissions?.find(
      (object) =>
        object.key === inboundProcessPermission.admin ||
        object.key === inboundProcessPermission.logisticsInbound
    );

    if (
      userGroup === groupTypes.brand ||
      userGroup === groupTypes.supplier ||
      hasRequestsPermissions ||
      hasASNPermissions
    ) {
      if (
        userGroup === groupTypes.brand ||
        userGroup === groupTypes.supplier ||
        hasRequestsPermissions
      ) {
        userTabs.push(requestsTab);
      }

      // Tab temporarily removed as it is not used yet
      // if (hasASNPermissions) {
      //   userTabs.push(asnTab);
      // }

      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    return userTabs;
  };

  const generateClientManagementTabsBasedOnUserPermissions = () => {
    const customersTab = {
      label: "Companies",
      value: "companies",
    };
    const contactsTab = {
      label: "Contacts",
      value: "contacts",
    };

    // User Tabs based on Permissions
    const userTabs = [];

    // Tomorrow Crew Admin
    const hasPermissions = userPermissions?.find(
      (object) =>
        object.key === clientManagementTomorrowCrewPermission.admin ||
        object.key === clientManagementTomorrowCrewPermission.clientManagement
    );

    if (hasPermissions) {
      userTabs.push(customersTab, contactsTab);
      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    return userTabs;
  };

  const generateBookingSystemTabsBasedOnUserPermissions = () => {
    const allBookingsTab = {
      label: "All Bookings",
      value: "all-bookings",
    };
    const skippedTab = {
      label: "Skipped",
      value: "booking-system-skipped",
    };
    const settingsTab = {
      label: "Settings",
      value: "booking-system-settings",
    };

    // User Tabs based on Permissions
    const userTabs = [];

    // Tomorrow Crew Admin
    const hasPermissions = userPermissions?.find(
      (object) =>
        object.key === bookingSystemTomorrowCrewPermissions.admin ||
        object.key ===
        bookingSystemTomorrowCrewPermissions.bookingSystemAdmin ||
        object.key === bookingSystemTomorrowCrewPermissions.bookingSystemUser
    );

    if (hasPermissions) {
      userTabs.push(allBookingsTab, skippedTab, settingsTab);
      return { userTabs, selectedTab: userTabs[0]?.value };
    }

    return userTabs;
  };

  // Tabs Settings
  const priceSetupTabsSettings = generatePriceSetupTabsBasedOnUserPermissions();
  const priceSetupTabs = priceSetupTabsSettings.userTabs;

  const priceSetupElements = priceSetupTabs?.map((tab) => {
    return {
      id: tab.value,
      type: "button",
      label: tab.label,
      handler: null,
      href: `price-setup/${tab.label.toLowerCase()}`,
    };
  });

  const linesheetSettingsTabsSettings =
    generateLinesheetSettingsTabsBasedOnUserPermissions();
  const linesheetSettingsTabs = linesheetSettingsTabsSettings.userTabs;

  const linesheetSettingsElements = linesheetSettingsTabs?.map((tab) => {
    return {
      id: tab.value,
      type: "button",
      label: tab.label,
      handler: null,
      href: `linesheet-settings/${tab.value}`,
    };
  });

  const inboundElementsTabsSettings =
    generateInboundElementsTabsBasedOnUserPermissions();
  const inboundElementsTabs = inboundElementsTabsSettings.userTabs;

  const inboundElements = inboundElementsTabs?.map((tab) => {
    return {
      id: tab.value,
      type: "button",
      label: tab.label,
      handler: null,
      href: `inbound/${tab.value}`,
    };
  });

  const clientManagementTabsSettings =
    generateClientManagementTabsBasedOnUserPermissions();
  const clientManagementTabs = clientManagementTabsSettings.userTabs;

  const clientManagementElements = clientManagementTabs?.map((tab) => {
    return {
      id: tab.value,
      type: "button",
      label: tab.label,
      handler: null,
      href: `client-management/${tab.value}`,
    };
  });

  const bookingSystemTabsSettings =
    generateBookingSystemTabsBasedOnUserPermissions();
  const bookingSystemTabs = bookingSystemTabsSettings.userTabs;

  const bookingSystemElements = bookingSystemTabs?.map((tab) => {
    return {
      id: tab.value,
      type: "button",
      label: tab.label,
      handler: null,
      href: `booking-system/${tab.value}`,
    };
  });

  return (
    <Stack
      id="topnav-tabs-horizontal-scroll"
      component="ul"
      direction="row"
      spacing={1}
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        margin: "0 25px",
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "none",
      }}
    >
      {items.map((item) => {
        const checkPath = !!(item.path && pathname);
        const partialMatch = checkPath ? pathname.includes(item.path) : false;
        const exactMatch = checkPath ? pathname === item.path : false;

        // Branch

        if (item.items) {
          return (
            <TopNavItem
              active={partialMatch}
              disabled={item.disabled}
              icon={item.icon}
              items={item.items}
              key={item.title}
              label={item.label}
              title={item.title}
            />
          );
        }

        // Leaf
        if (item.title === "Price Setup") {
          return (
            <CustomDropdown
              key={item.title}
              buttons={priceSetupElements}
              label="PRICE SETUP"
              style={{
                width: "170px",
                margin: "0 10px 0 0",
                backgroundColor: partialMatch ? "#E6E6E6" : "white",
              }}
              dropdownStyle={{ width: "170px" }}
              router
            />
          );
        }
        if (item.title === "Client Management") {
          return (
            <CustomDropdown
              key={item.title}
              buttons={clientManagementElements}
              label="CLIENT MANAGEMENT"
              style={{
                width: "230px",
                margin: "0 10px 0 0",
                backgroundColor: partialMatch ? "#E6E6E6" : "white",
              }}
              dropdownStyle={{ width: "230px" }}
              router
            />
          );
        }
        if (item.title === "Booking System") {
          return (
            <CustomDropdown
              key={item.title}
              buttons={bookingSystemElements}
              label="BOOKING SYSTEM"
              style={{
                width: "210px",
                margin: "0 10px 0 0",
                backgroundColor: partialMatch ? "#E6E6E6" : "white",
              }}
              dropdownStyle={{ width: "210px" }}
              router
            />
          );
        }
        if (item.title === "Linesheet Settings") {
          return (
            <CustomDropdown
              key={item.title}
              buttons={linesheetSettingsElements}
              label="LINESHEET SETTINGS"
              style={{
                width: "230px",
                margin: "0 10px 0 0",
                backgroundColor: partialMatch ? "#E6E6E6" : "white",
              }}
              dropdownStyle={{ width: "230px" }}
              router
            />
          );
        }
        if (item.title === "Inbound") {
          return (
            <CustomDropdown
              key={item.title}
              buttons={inboundElements}
              label="INBOUND"
              style={{
                width: "170px",
                margin: "0 10px 0 0",
                backgroundColor: partialMatch ? "#E6E6E6" : "white",
              }}
              dropdownStyle={{ width: "170px" }}
              router
            />
          );
        } else {
          return (
            <TopNavItem
              active={partialMatch}
              disabled={item.disabled}
              external={item.external}
              key={item.title}
              label={item.label}
              path={item.path}
              title={item.title}
            />
          );
        }
      })}
    </Stack>
  );
};

TopNavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  subheader: PropTypes.string,
};
